<template>
    <div>
        <timeline :active-index="2" :time-line-items="steps">
            <template class="relative" v-slot:form>
                <div class="card card-custom mt-12 absolute">
                    <div class="card-header">
                        <div class="card-title">Ihre Bestellung</div>
                    </div>
                    <div class="card-body">
                        <div class="d-flex flex-row mb-8 font-weight-bolder font-size-lg">
                            <div class="col-3 pl-0 text-xl">{{ __('user.plan') }}</div>
                            <div class="col-2 px-0 text-xl">{{ __('general.plan') }}</div>
                            <div class="col-3 px-0 text-xl">
                                <div class="text-xl">{{ __('general.price') }}</div>
                                <div class="text-muted font-italic font-size-sm">( {{ __('general.net') }} )</div>
                            </div>
                            <div class="col-1 px-0"></div>
                            <div class="col-3 text-right px-0">
                                <div class="text-xl">{{ __('general.price_total') }}</div>
                                <div class="text-muted font-italic font-size-sm">{{ __('account-setup.include_19_tax') }}</div>
                            </div>
                        </div>
                        <div class="d-flex flex-row mb-8">
                            <div class="col-3 pl-0">{{ getTranslation(bundle.lang.name) }}</div>
                            <div class="col-2 px-0">
                                <span class="text-2xl font-weight-bolder">{{ storeCount }}</span>
                                <span class="text-muted font-italic">{{ __('user.stores') }}</span>
                            </div>
                            <div class="col-3 px-0">
                                <Price class="font-weight-bolder text-2xl"
                                       :value="bundle.pricing_groups[savedPricingGroup][0].value"
                                       :currency="bundle.pricing_groups[savedPricingGroup][0].currency"></Price>
                                <span class="text-muted font-italic">{{ __('general.per_store') }}</span>
                            </div>
                            <div class="col-1 px-0">{{
                                    __(`account-setup.invoicing_period.${bundle.pricing_groups[savedPricingGroup][0].invoicing_period}`)
                                }}
                            </div>
                            <div class="col-3 px-0 text-2xl font-weight-bolder text-right">
                                <Price :value="perMonthTotal" :currency="bundle.pricing_groups[savedPricingGroup][0].currency"></Price>
                            </div>
                        </div>

                        <div v-if="!addonsEmpty">
                            <hr/>
                            <div class="d-flex flex-row align-items-center mb-8 font-weight-bolder">
                                <div class="col-3 pl-0">{{ __('subscriptions.addons') }}</div>
                                <div class="col-2 px-0"></div>
                                <div class="col-3 px-0"></div>
                                <div class="col-1 px-0"></div>
                                <div class="col-3 px-0"></div>
                            </div>
                        </div>

                        <div v-for="addon in perStoreAddons" class="d-flex flex-row my-4 min-h-40px">
                            <div class="col-3 pl-0">{{ getTranslation(addon.lang.name) }}</div>
                            <div class="col-2 px-0">
                                <span class="font-weight-bolder text-2xl">{{ storeCount }} </span>
                                <span class="text-muted font-italic">{{ __('user.stores') }}</span>
                            </div>
                            <div class="col-3 px-0">
                                <Price class="font-weight-bolder text-2xl"
                                       :value="addon.pricing_groups.all[0].value"
                                       :currency="addon.pricing_groups.all[0].currency"></Price>
                                <span class="text-muted font-italic">{{ __('general.per_store') }}</span>
                            </div>
                            <div class="col-1 px-0">{{
                                    __(`account-setup.invoicing_period.${addon.pricing_groups.all[0].invoicing_period}`)
                                }}
                            </div>
                            <div class="col-3 px-0 text-right">
                                <Price class="text-2xl font-weight-bolder"
                                       :value="addonTimesStoreCountPrice(addon)"
                                       :currency="addon.pricing_groups.all[0].currency"></Price>
                            </div>
                        </div>

                        <div v-for="addon in perAccountAddons" class="d-flex flex-row my-4 min-h-40px">
                            <div class="col-3 pl-0">{{ getTranslation(addon.lang.name) }}</div>
                            <div class="col-2 px-0">
                                <span class="font-weight-bolder text-2xl">1</span>
                                <span class="text-muted font-italic">Account</span>
                            </div>
                            <div class="col-3 px-0">
                                <Price class="font-weight-bolder text-2xl"
                                       :value="addon.pricing_groups.all[0].value"
                                       :currency="addon.pricing_groups.all[0].currency"></Price>
                            </div>
                            <div class="col-1 px-0">{{
                                    __(`account-setup.invoicing_period.${addon.pricing_groups.all[0].invoicing_period}`)
                                }}
                            </div>
                            <div class="col-3 px-0 text-right">
                                <Price class="text-2xl font-weight-bolder"
                                       :value="addon.pricing_groups.all[0].value"
                                       :currency="addon.pricing_groups.all[0].currency"></Price>
                            </div>
                        </div>

                        <hr/>
                        <div class="d-flex flex-row align-items-center">
                            <div class="col-9 text-xl text-right">{{ __('account-setup.total_due') }}</div>
                            <div class="col-3 px-0 font-weight-bolder text-3xl text-right">
                                <Price :value="finalTotal" :currency="bundle.pricing_groups[savedPricingGroup][0].currency"></Price>
                            </div>
                        </div>

                        <hr/>

                        <div class="text-right font-italic text-lg font-weight-light text-warning my-8">{{ __('subscriptions.recurring_payment_warn') }}</div>
                        <div class="d-flex flex-row">
                            <div class="col-9 px-0 text-right text-lg">{{ __('subscriptions.recurring_payment_month') }}</div>
                            <div class="col-3 px-0 text-2xl font-weight-bolder text-right">
                                <Price :value="perMonthTotal" :currency="bundle.pricing_groups[savedPricingGroup][0].currency"></Price>
                            </div>
                        </div>
                        <div class="d-flex flex-row mb-8">
                            <div class="col-9 px-0 text-right text-muted font-italic font-size-sm">{{ __('account-setup.include_19_tax') }}</div>
                        </div>
                    </div>

                    <div class="card-footer text-center">
                        <div v-if="hasPaymentMethodSet && !showSpinner" @click="submit" class="btn btn-primary">{{ __('subscriptions.subscribe_payment') }}</div>
                        <div v-if="!showSpinner && !paymentSuccess && !hasPaymentMethodSet" @click="setPaymentMethod" class="btn btn-primary mx-auto">
                            {{ __('subscriptions.book') }}
                        </div>
                        <div v-if="showSpinner">
                            <div v-if="!paymentSuccess" class="text-primary text-xl mb-8">{{ __('account-setup.order_save_payment_data') }}</div>
                            <div v-else class="text-primary text-xl mb-8">{{ __('subscriptions.subscription_spinner') }}</div>
                            <div class="spinner spinner-primary spinner-center spinner-lg"></div>
                        </div>
                    </div>
                </div>

                <div class="d-flex mt-20">
                    <button @click="previousStep" class="mr-auto py-4 btn btn-secondary">
                        <i class="fas fa-arrow-circle-left mr-3"></i>{{ __('actions.back') }}
                    </button>
                </div>

            </template>
        </timeline>
    </div>
</template>

<script>
import AccountSetupLayout from '@layouts/AccountSetupLayout';
import Timeline from '@components/Timeline';
import Price from '@components/Price';
import Spinner from 'vue-simple-spinner';
import WebsocketMixin from '@mixins/WebsocketMixin';
import helperMethods from '@mixins/helperMethods'

export default {
    layout: AccountSetupLayout,
    components: {Price, Timeline, Spinner},
    mixins: [WebsocketMixin, helperMethods],
    props: {
        steps: Object,
        savedPricingGroup: String,
        bundle: Object,
        addons: Object | Array,
        storeCount: Number,
        stripeUrl: String,
        hasPaymentMethodSet: Boolean,
    },

    data() {
        return {
            showSpinner: false,
            paymentSuccess: false,
        };
    },

    mounted() {
        this.listenAuthUser('payment.method.attached', this.paymentMethodAttached);
    },

    methods: {
        previousStep() {
            this.$inertia.get(this.$route('account-setup-addons.show'));
        },

        retryHasPaymentMethodSet() {
            this.$inertia.reload({
                only: ['hasPaymentMethodSet'],
                onError: () => {
                    toastr.error(__('general.error_check_data'));
                },
                onSuccess: (response) => {
                    if (!response.props.hasPaymentMethodSet) {
                        setTimeout(() => {
                            this.retryHasPaymentMethodSet();
                        }, 3000);

                        return;
                    }

                    this.paymentSuccess = true;
                    this.submit();
                },
            });
        },

        setPaymentMethod() {
            Swal.fire({
                title: __('account-setup.payment_data.save'),
                text: __('account-setup.payment_data.redirect'),
                type: 'warning',
                showCancelButton: true,
                confirmButtonClass: 'btn btn-primary m-0',
                confirmButtonText: __('actions.proceed_thing', {thing: 'stripe.com'}),
                cancelButtonText: __('actions.abort'),
            }).then((result) => {
                if (result.value) {
                    this.showSpinner = true;
                    window.open(this.stripeUrl, '_blank');

                    this.retryHasPaymentMethodSet();
                }
            });
        },

        submit() {
            this.$inertia.post(this.$route('account-setup-payment.update'), {}, {
                preserveState: true,
                preserveScroll: true,
                onError: () => {
                    toastr.error(__('general.error_check_data'));
                },
                onSuccess: () => {
                    toastr.success(__('account-setup.thing.success_single', {thing: __('account-setup.your_subscription')}));
                },
            });
        },

        addonTimesStoreCountPrice(addon) {
            return this.storeCount * addon.pricing_groups.all[0].value;
        },

        paymentMethodAttached() {
            this.paymentSuccess = true;

            this.submit();
        },
    },

    computed: {
        addonsEmpty() {
            return _.isEmpty(this.addons);
        },

        perStoreAddons() {
            return Object.values(this.addons).filter((addon) => {
                return addon.pricing_groups.all[0].invoicing_type === 'per_store';
            });
        },

        perAccountAddons() {
            return Object.values(this.addons).filter((addon) => {
                return addon.pricing_groups.all[0].invoicing_type === 'per_user';
            });
        },

        addonTotal() {
            let total = 0;
            this.perStoreAddons.forEach((addon) => {
                total += this.storeCount * addon.pricing_groups.all[0].value;
            });

            this.perAccountAddons.forEach((addon) => {
                total += addon.pricing_groups.all[0].value;
            });

            return total;
        },

        perMonthTotal() {
            return this.storeCount * this.bundle.pricing_groups[this.savedPricingGroup][0].value;
        },

        finalTotal() {
            return (this.addonTotal + (this.storeCount * this.bundle.pricing_groups[this.savedPricingGroup][0].value));
        },
    },
};
</script>
